import type { i18n as I18n } from 'i18next';
import type { FieldKind } from 'v5/core/entity-schema/entitySchema.types';
import type {
  ValidationIssue,
  ValidationPart
} from 'v5/core/entity-values/values.types';
import { createRateIssueMessage } from './rate/rateIssueMessage';

/**
 * Each field kind's sentence for a missing part (`SchemaForm.partRequired`):
 * a whole sentence per part, because a part's name pasted into a shared
 * template can't agree with it in every language.
 */
const PART_REQUIRED = {
  name: { 'first-name': 'name.firstName', 'last-name': 'name.lastName' },
  'contact-details': {
    type: 'contactDetails.type',
    value: 'contactDetails.value'
  },
  currency: { currency: 'currency.currency' },
  'measurement-legacy': { unit: 'measurementLegacy.unit' },
  tags: { 'tag-name': 'tags.tagName' },
  groups: { 'group-name': 'groups.groupName' }
} as const satisfies {
  [K in FieldKind]?: { [P in ValidationPart]?: string };
};

type PartRequiredKey = {
  [K in keyof typeof PART_REQUIRED]: (typeof PART_REQUIRED)[K][keyof (typeof PART_REQUIRED)[K]];
}[keyof typeof PART_REQUIRED];

/** The table as a lookup by any kind and part. */
const partRequiredKeys: {
  [K in FieldKind]?: { [P in ValidationPart]?: PartRequiredKey };
} = PART_REQUIRED;

/**
 * The field kind's own message for an issue about one of its parts, or `null`
 * where the kind has none (the generic message is used then). A rate speaks
 * with the rate editor's copy.
 */
export function createPartIssueMessage(
  i18n: I18n
): (kind: FieldKind, issue: ValidationIssue) => string | null {
  const rateMessage = createRateIssueMessage(i18n);
  const t = i18n.getFixedT(null, 'patterns', 'SchemaForm.partRequired');
  return (kind, issue) => {
    if (kind === 'rate') return rateMessage(issue);
    if (issue.code !== 'part-required') return null;
    const key = partRequiredKeys[kind]?.[issue.part];
    return key === undefined ? null : t(key);
  };
}
